/**
 * useFoundAnswers — the 30s poll behind "Merlin found answers" (kanban
 * 3eed0be5): the candidates the daily sweep holds for the reader's one-click
 * confirm, plus what it closed this week. Owned by the Quality Triage shell
 * (one poll for the door, like useMerlinFollowups); pages read it from the
 * Outlet context and never start their own.
 *
 * Goes dark when the deploy has no found-answers API (404) or the reader has
 * no grant for it (403): the strip then stays hidden.
 */
import { useCallback, useEffect, useState } from 'react';

import { listFoundAnswers, type FoundAnswerRead } from '../../services/qcpFoundAnswers';

const POLL_INTERVAL_MS = 30000; // matches the door's other polls

export interface FoundAnswersState {
  rows: FoundAnswerRead[];
  /** False when the API is absent (404) or forbidden (403) on this deploy. */
  available: boolean;
  /** True once the first answer (or refusal) has landed. */
  loaded: boolean;
  refresh: () => void;
  /** Fold a mutated row (confirm / still open) into the list at once. */
  applyUpdate: (updated: FoundAnswerRead) => void;
}

export function useFoundAnswers(enabled: boolean): FoundAnswersState {
  const [rows, setRows] = useState<FoundAnswerRead[]>([]);
  const [available, setAvailable] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    listFoundAnswers()
      .then((data) => {
        setAvailable(true);
        setRows(data);
        setLoaded(true);
      })
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 403) {
          setAvailable(false);
          setLoaded(true);
        }
        // otherwise: polling is best-effort; keep what we have
      });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, refresh]);

  const applyUpdate = useCallback((updated: FoundAnswerRead) => {
    setRows((prev) => {
      const next = prev.map((r) => (r.id === updated.id ? updated : r));
      // A declined row leaves the strip (status open); a confirmed one
      // moves to the closed fold.
      return next.filter((r) => r.status === 'candidate' || r.status === 'retired');
    });
  }, []);

  return { rows, available, loaded, refresh, applyUpdate };
}
